import { useNavigate } from "react-router-dom";
import PublicNav from "@/components/PublicNav";
import Footer, { startLogin } from "@/components/Footer";
import { bookConsultation } from "@/components/ConsultCTA";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  BrainCircuit, Calculator, TrendingUp, Activity, CalendarClock, Ruler, Layers,
  Users, ShieldAlert, Scale, ArrowRight, ArrowDown, CheckCircle2, X, Minus,
  Sparkles, Cpu, User, Database, FileText, MessageSquare, Lightbulb
} from "lucide-react";

const ECOSYSTEM = [
  { name: "NCIS™", tag: "Core Intelligence Platform", status: "ACTIVE", icon: BrainCircuit, desc: "The central construction intelligence engine powering every recommendation." },
  { name: "NCIS Estimate Engine™", status: "ACTIVE", icon: Calculator, desc: "Generates intelligent construction estimates using engineering assumptions, project specifications and dynamic pricing." },
  { name: "NCIS Cost Intelligence™", status: "ACTIVE", icon: TrendingUp, desc: "Package-wise construction cost engine with real-time cost impact analysis and Cost Tree visualization." },
  { name: "NCIS Construction Health Engine™", status: "ACTIVE", icon: Activity, desc: "Analyzes uploaded project documents to generate construction health scores, identify risks and recommend corrective actions." },
  { name: "NCIS Project Intelligence™", status: "COMING SOON", icon: CalendarClock, desc: "AI-powered construction planning, scheduling and milestone tracking." },
  { name: "NCIS Quantity Intelligence™", status: "COMING SOON", icon: Ruler, desc: "AI-generated BOQs and quantity take-offs from drawings." },
  { name: "NCIS Material Intelligence™", status: "COMING SOON", icon: Layers, desc: "Material recommendations, brand comparisons and lifecycle intelligence." },
  { name: "NCIS Partner Intelligence™", status: "COMING SOON", icon: Users, desc: "AI-assisted matching of homeowners with verified construction professionals." },
  { name: "NCIS Risk Intelligence™", status: "COMING SOON", icon: ShieldAlert, desc: "Predict construction risks, delays and budget overruns before they happen." },
  { name: "NCIS Compliance Intelligence™", status: "COMING SOON", icon: Scale, desc: "Building bylaw compliance, FAR calculations and approval guidance." },
];

const FLOW = [
  { label: "Property Owner", icon: User },
  { label: "Project Data", icon: Database },
  { label: "NCIS™", icon: Cpu, core: true },
  { label: "Estimate Engine", icon: Calculator },
  { label: "Cost Intelligence", icon: TrendingUp },
  { label: "Health Engine", icon: Activity },
  { label: "Recommendations", icon: Lightbulb },
  { label: "Expert Consultation", icon: MessageSquare },
];

const COMPARE_COLS = ["Traditional Construction", "Generic AI", "NCIS™"];
const COMPARE_ROWS = [
  { label: "Project-specific analysis", v: ["partial", "no", "yes"] },
  { label: "Construction-specific knowledge", v: ["partial", "no", "yes"] },
  { label: "Engineering assumptions", v: ["partial", "no", "yes"] },
  { label: "Dynamic cost engine", v: ["no", "no", "yes"] },
  { label: "Real-time package pricing", v: ["no", "no", "yes"] },
  { label: "Material intelligence", v: ["partial", "no", "yes"] },
  { label: "Decision support", v: ["no", "partial", "yes"] },
];

const Mark = ({ kind }) => {
  if (kind === "yes") return <CheckCircle2 className="h-5 w-5 text-[#0E9AC8]" strokeWidth={2} />;
  if (kind === "partial") return <Minus className="h-5 w-5 text-amber-400" strokeWidth={2.5} />;
  return <X className="h-5 w-5 text-slate-300" strokeWidth={2} />;
};

export default function Technology() {
  const navigate = useNavigate();
  const { user } = useAuth();

  return (
    <div className="min-h-screen bg-white">
      <PublicNav />

      {/* HERO */}
      <section className="relative overflow-hidden bg-[#0B1B2B] text-white">
        <div className="absolute inset-0 ncis-grid-bg opacity-70" />
        <div className="absolute -top-24 -right-24 h-96 w-96 rounded-full bg-[#0E9AC8]/25 blur-[120px]" />
        <div className="absolute -bottom-32 -left-24 h-96 w-96 rounded-full bg-[#0E9AC8]/15 blur-[120px]" />
        <div className="relative max-w-6xl mx-auto px-6 pt-20 pb-24 text-center">
          <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-1.5 text-xs font-semibold tracking-wide text-white/80 animate-fade-up" data-testid="tech-eyebrow">
            <Sparkles className="h-3.5 w-3.5 text-[#4FD1DB]" /> POWERED BY NCIS™
          </div>
          <p className="mt-6 text-sm font-semibold uppercase tracking-[0.25em] text-[#4FD1DB] animate-fade-up" style={{ animationDelay: "60ms" }}>
            Nirmantra Construction Intelligence System
          </p>
          <h1 className="mt-5 font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter leading-[1.05] animate-fade-up" style={{ animationDelay: "120ms" }} data-testid="tech-headline">
            Construction Intelligence.<br />
            <span className="text-[#4FD1DB]">Not Just Artificial Intelligence.</span>
          </h1>
          <p className="mt-6 max-w-3xl mx-auto text-base sm:text-lg text-white/70 leading-relaxed animate-fade-up" style={{ animationDelay: "180ms" }}>
            NCIS™ is Nirmantra.ai's proprietary technology platform that combines AI, engineering knowledge,
            construction standards, building bylaws, quantity surveying, cost intelligence and real-world project
            expertise to help property owners make smarter construction decisions.
          </p>
          <div className="mt-9 flex flex-col sm:flex-row items-center justify-center gap-3 animate-fade-up" style={{ animationDelay: "240ms" }}>
            <Button data-testid="tech-cta-primary" onClick={startLogin}
              className="bg-[#0E9AC8] hover:bg-[#0B7EA4] text-white rounded-xl h-12 px-7 text-sm font-semibold shadow-lg shadow-[#0E9AC8]/20 transition-colors">
              Generate Free Construction Health Report <ArrowRight className="ml-2 h-4 w-4" strokeWidth={2} />
            </Button>
            <Button data-testid="tech-cta-secondary" variant="outline" onClick={() => bookConsultation(navigate, user)}
              className="rounded-xl h-12 px-7 text-sm font-semibold border-white/25 bg-transparent text-white hover:bg-white/10 hover:text-white transition-colors">
              Book Expert Consultation
            </Button>
          </div>
        </div>
      </section>

      {/* ECOSYSTEM */}
      <section className="max-w-7xl mx-auto px-6 py-20">
        <div className="text-center max-w-2xl mx-auto">
          <p className="text-sm font-semibold uppercase tracking-wider text-[#0E9AC8]">The NCIS Ecosystem</p>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">One intelligence platform. Ten engines.</h2>
          <p className="mt-4 text-slate-500">Every Nirmantra recommendation is powered by a connected system of specialised construction intelligence engines.</p>
        </div>
        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="tech-ecosystem">
          {ECOSYSTEM.map((c, i) => {
            const active = c.status === "ACTIVE";
            return (
              <div key={c.name} data-testid={`tech-card-${i}`}
                className="group card-premium rounded-2xl p-6 animate-fade-up transition-all duration-300 hover:-translate-y-1.5 hover:border-[#0E9AC8]/60 hover:shadow-[0_18px_40px_-24px_rgba(14,154,200,0.55)]"
                style={{ animationDelay: `${i * 55}ms` }}>
                <div className="flex items-start justify-between">
                  <div className={`h-12 w-12 rounded-xl flex items-center justify-center transition-colors ${active ? "bg-[#0E9AC8]/10 text-[#0E9AC8] group-hover:bg-[#0E9AC8] group-hover:text-white" : "bg-slate-100 text-slate-400 group-hover:bg-slate-900 group-hover:text-white"}`}>
                    <c.icon className="h-6 w-6" strokeWidth={1.6} />
                  </div>
                  <span data-testid={`tech-card-status-${i}`}
                    className={`inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full border ${active ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-50 text-slate-500 border-slate-200"}`}>
                    {active && <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 ncis-glow" />}
                    {c.status}
                  </span>
                </div>
                <h3 className="mt-5 font-display text-lg font-extrabold text-slate-900">{c.name}</h3>
                {c.tag && <p className="text-xs font-semibold text-[#0E9AC8] mt-0.5">{c.tag}</p>}
                <p className="mt-2 text-sm text-slate-500 leading-relaxed">{c.desc}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* ARCHITECTURE DIAGRAM */}
      <section className="relative bg-[#0B1B2B] text-white overflow-hidden">
        <div className="absolute inset-0 ncis-grid-bg opacity-60" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-[520px] w-[520px] rounded-full bg-[#0E9AC8]/10 blur-[140px]" />
        <div className="relative max-w-3xl mx-auto px-6 py-20">
          <div className="text-center max-w-2xl mx-auto">
            <p className="text-sm font-semibold uppercase tracking-wider text-[#4FD1DB]">How it works</p>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl font-extrabold tracking-tight">The NCIS Intelligence Pipeline</h2>
            <p className="mt-4 text-white/60">From your project data to expert-backed decisions — every step flows through NCIS™.</p>
          </div>
          <div className="mt-14 flex flex-col items-center" data-testid="tech-architecture">
            {FLOW.map((n, i) => (
              <div key={n.label} className="w-full flex flex-col items-center">
                <div data-testid={`tech-flow-${i}`}
                  className={`w-full max-w-sm animate-fade-up flex items-center gap-4 rounded-2xl border px-5 py-4 backdrop-blur-sm transition-transform hover:scale-[1.02]
                    ${n.core ? "border-[#0E9AC8] bg-[#0E9AC8]/15 ncis-float shadow-lg shadow-[#0E9AC8]/20" : "border-white/12 bg-white/[0.04]"}`}
                  style={{ animationDelay: `${i * 90}ms` }}>
                  <div className={`h-11 w-11 shrink-0 rounded-xl flex items-center justify-center ${n.core ? "bg-[#0E9AC8] text-white" : "bg-white/10 text-[#4FD1DB]"}`}>
                    <n.icon className="h-5 w-5" strokeWidth={1.75} />
                  </div>
                  <div>
                    <p className={`font-display font-bold ${n.core ? "text-xl text-white" : "text-base text-white/90"}`}>{n.label}</p>
                    {n.core && <p className="text-xs text-[#4FD1DB]">Core Intelligence Engine</p>}
                  </div>
                </div>
                {i < FLOW.length - 1 && (
                  <div className="relative h-10 w-px my-1 bg-white/15 overflow-hidden">
                    <span className="ncis-flow-dot absolute left-1/2 -translate-x-1/2 h-3 w-3 rounded-full bg-[#4FD1DB]" style={{ animationDelay: `${i * 0.25}s` }} />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY DIFFERENT */}
      <section className="max-w-5xl mx-auto px-6 py-20">
        <div className="text-center max-w-2xl mx-auto">
          <p className="text-sm font-semibold uppercase tracking-wider text-[#0E9AC8]">The difference</p>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">Why NCIS is Different</h2>
          <p className="mt-4 text-slate-500">Generic AI guesses. Traditional methods are manual. NCIS™ is purpose-built for construction.</p>
        </div>

        <div className="mt-12 card-premium rounded-2xl overflow-hidden" data-testid="tech-compare">
          <div className="grid grid-cols-[1.6fr_1fr_1fr_1fr] bg-slate-900 text-white text-sm">
            <div className="px-5 py-4 font-semibold">Capability</div>
            {COMPARE_COLS.map((c) => (
              <div key={c} className={`px-3 py-4 text-center font-semibold ${c === "NCIS™" ? "bg-[#0E9AC8]" : ""}`}>{c}</div>
            ))}
          </div>
          {COMPARE_ROWS.map((r, ri) => (
            <div key={r.label} data-testid={`tech-compare-row-${ri}`}
              className={`grid grid-cols-[1.6fr_1fr_1fr_1fr] items-center border-t border-slate-100 ${ri % 2 ? "bg-slate-50/60" : "bg-white"}`}>
              <div className="px-5 py-4 text-sm font-medium text-slate-800">{r.label}</div>
              {r.v.map((val, ci) => (
                <div key={ci} className={`px-3 py-4 flex justify-center ${ci === 2 ? "bg-[#0E9AC8]/5" : ""}`}>
                  <Mark kind={val} />
                </div>
              ))}
            </div>
          ))}
        </div>
        <div className="mt-5 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-xs text-slate-500">
          <span className="flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-[#0E9AC8]" /> Full capability</span>
          <span className="flex items-center gap-1.5"><Minus className="h-4 w-4 text-amber-400" /> Partial / manual</span>
          <span className="flex items-center gap-1.5"><X className="h-4 w-4 text-slate-300" /> Not available</span>
        </div>
      </section>

      {/* CLOSING CTA */}
      <section className="max-w-6xl mx-auto px-6 pb-24">
        <div className="relative overflow-hidden rounded-3xl bg-[#0B1B2B] text-white px-8 py-14 text-center">
          <div className="absolute inset-0 ncis-grid-bg opacity-50" />
          <div className="absolute -top-20 right-10 h-72 w-72 rounded-full bg-[#0E9AC8]/25 blur-[110px]" />
          <div className="relative">
            <h2 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight">Put NCIS™ to work on your project.</h2>
            <p className="mt-4 max-w-xl mx-auto text-white/70">Get a free, AI-powered Construction Health Report — or talk to an independent expert about your build.</p>
            <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
              <Button data-testid="tech-cta-bottom-primary" onClick={startLogin}
                className="bg-[#0E9AC8] hover:bg-[#0B7EA4] text-white rounded-xl h-12 px-7 text-sm font-semibold transition-colors">
                Generate Free Construction Health Report <ArrowRight className="ml-2 h-4 w-4" strokeWidth={2} />
              </Button>
              <Button data-testid="tech-cta-bottom-secondary" variant="outline" onClick={() => bookConsultation(navigate, user)}
                className="rounded-xl h-12 px-7 text-sm font-semibold border-white/25 bg-transparent text-white hover:bg-white/10 hover:text-white transition-colors">
                Book Expert Consultation
              </Button>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
